import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtIDR, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Download, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const GROUPS = [
  "Kelompok 1", "Kelompok 2", "Kelompok 3", "Kelompok 4",
  "Bangunan Permanen", "Bangunan Tidak Permanen"
];

const DaftarAset = () => {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [rules, setRules] = useState({});
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({
    name: "", group: "Kelompok 2", method: "garis_lurus",
    acquisition_date: todayISO(), acquisition_cost: "", salvage_value: 0, note: "",
  });

  const load = () => api.get("/assets").then(r => setAssets(r.data));
  useEffect(() => { load(); api.get("/depreciation-rules").then(r => setRules(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/assets", {
        ...form,
        acquisition_cost: parseFloat(form.acquisition_cost),
        salvage_value: parseFloat(form.salvage_value || 0),
      });
      toast.success("Aset ditambahkan"); setOpen(false); load();
      setForm({ name: "", group: "Kelompok 2", method: "garis_lurus", acquisition_date: todayISO(), acquisition_cost: "", salvage_value: 0, note: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const download = () => {
    const token = localStorage.getItem("spbu_token");
    fetch(`${API}/export/daftar-aset.xlsx`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "daftar_aset.xlsx"; a.click();
      });
  };

  const del = async (id) => {
    if (!window.confirm("Hapus aset ini?")) return;
    await api.delete(`/assets/${id}`); toast.success("Terhapus"); load();
  };

  const groupRule = rules[form.group];
  const canDB = groupRule && groupRule.db !== null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Daftar Aset & Penyusutan</h1>
          <p className="text-sm text-slate-500 mt-1">Penyusutan fiskal sesuai PMK 72/2023 & UU PPh Pasal 11</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={download} variant="outline"><Download className="w-4 h-4 mr-2" />Excel</Button>
          {user?.role === "admin" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="add-asset-button" className="bg-[#E30613] hover:bg-[#C8040F]"><Plus className="w-4 h-4 mr-2" />Tambah Aset</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Tambah Aset Tetap</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div><Label>Nama Aset</Label><Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Dispenser Wayne 4 Nozzle" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Kelompok Harta</Label>
                      <Select value={form.group} onValueChange={(v) => setForm(f => ({ ...f, group: v, method: rules[v]?.db === null ? "garis_lurus" : f.method }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Metode</Label>
                      <Select value={form.method} onValueChange={(v) => setForm(f => ({ ...f, method: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="garis_lurus">Garis Lurus</SelectItem>
                          {canDB && <SelectItem value="saldo_menurun">Saldo Menurun</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {groupRule && (
                    <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                      Masa manfaat: <b>{groupRule.years} tahun</b> · Tarif GL: <b>{(groupRule.sl * 100).toFixed(2)}%</b>
                      {groupRule.db && <> · Tarif SM: <b>{(groupRule.db * 100).toFixed(2)}%</b></>}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tanggal Perolehan</Label><Input type="date" value={form.acquisition_date} onChange={(e) => setForm(f => ({ ...f, acquisition_date: e.target.value }))} required /></div>
                    <div><Label>Harga Perolehan (Rp)</Label><Input type="number" value={form.acquisition_cost} onChange={(e) => setForm(f => ({ ...f, acquisition_cost: e.target.value }))} required /></div>
                  </div>
                  <div><Label>Nilai Sisa (Rp)</Label><Input type="number" value={form.salvage_value} onChange={(e) => setForm(f => ({ ...f, salvage_value: e.target.value }))} /></div>
                  <div><Label>Catatan</Label><Input value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} /></div>
                  <DialogFooter><Button type="submit" className="bg-[#E30613] hover:bg-[#C8040F]">Simpan Aset</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Klasifikasi Harta (PMK 72/2023)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(rules).map(([g, r]) => (
              <div key={g} className="rounded-lg border border-slate-200 p-3">
                <div className="font-bold text-sm">{g}</div>
                <div className="text-xs text-slate-500 mt-1">Masa manfaat {r.years} tahun</div>
                <div className="text-xs mt-2">GL: <span className="font-mono-fig font-semibold">{(r.sl * 100).toFixed(2)}%</span></div>
                {r.db && <div className="text-xs">SM: <span className="font-mono-fig font-semibold">{(r.db * 100).toFixed(2)}%</span></div>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Daftar Aset Tetap</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>Nama Aset</TableHead>
                <TableHead>Kelompok</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead>Tgl Perolehan</TableHead>
                <TableHead className="text-right">Harga Perolehan</TableHead>
                <TableHead className="text-right">Akum. Penyusutan</TableHead>
                <TableHead className="text-right">Nilai Buku</TableHead>
                {user?.role === "admin" && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map(a => (
                <React.Fragment key={a.id}>
                  <TableRow>
                    <TableCell><Button size="icon" variant="ghost" onClick={() => setExpanded(prev => ({ ...prev, [a.id]: !prev[a.id] }))}>{expanded[a.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</Button></TableCell>
                    <TableCell className="font-semibold">{a.name}</TableCell>
                    <TableCell><Badge variant="secondary">{a.group}</Badge></TableCell>
                    <TableCell className="text-sm capitalize">{a.method.replace("_", " ")}</TableCell>
                    <TableCell className="font-mono-fig text-sm">{a.acquisition_date}</TableCell>
                    <TableCell className="text-right font-mono-fig">{fmtIDR(a.acquisition_cost)}</TableCell>
                    <TableCell className="text-right font-mono-fig text-rose-700">{fmtIDR(a.accumulated_depreciation)}</TableCell>
                    <TableCell className="text-right font-mono-fig font-bold">{fmtIDR(a.current_book_value)}</TableCell>
                    {user?.role === "admin" && <TableCell><Button size="icon" variant="ghost" onClick={() => del(a.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></TableCell>}
                  </TableRow>
                  {expanded[a.id] && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-slate-50 p-4">
                        <div className="text-sm font-semibold mb-2">Jadwal Penyusutan Fiskal</div>
                        <div className="overflow-x-auto">
                          <table className="text-xs w-full">
                            <thead><tr className="text-slate-500"><th className="text-left py-1">Tahun</th><th className="text-right py-1">Penyusutan</th><th className="text-right py-1">Nilai Buku Akhir</th></tr></thead>
                            <tbody>
                              {a.schedule.map(s => (
                                <tr key={s.year} className="border-t border-slate-200">
                                  <td className="py-1 font-mono-fig">{s.year}</td>
                                  <td className="text-right py-1 font-mono-fig">{fmtIDR(s.depreciation)}</td>
                                  <td className="text-right py-1 font-mono-fig">{fmtIDR(s.book_value)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              {assets.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Belum ada aset</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DaftarAset;
